import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { ButtonPadraoComponent } from '../../shared/components/button-padrao/button-padrao.component';
import { FormFieldComponent } from '../../shared/components/form-field/form-field.component';
import { SlideTogglePadraoComponent } from '../../shared/components/slide-toggle-padrao/slide-toggle-padrao.component';
import { ParametroAzureAcsModel } from '../../shared/model/parametros/parametro-azure-acs.model';
import { MsgService } from '../../shared/service/msg.service';
import { ParametroAzureAcsService } from '../../shared/service/parametro-azure-acs.service';
import { SelectOneComponent } from '../../shared/components/select-one/select-one.component';

@Component({
  selector: 'app-card-azure-acs',
  standalone: true,
  imports: [SlideTogglePadraoComponent, FormFieldComponent, ButtonPadraoComponent, SelectOneComponent],
  templateUrl: './card-azure-acs.component.html',
  styleUrl: '../card-aws-ses/card-aws-ses.component.css'
})
export class CardAzureAcsComponent implements OnInit {
  private readonly parametroService = inject(ParametroAzureAcsService);
  private readonly msgService = inject(MsgService);
  private readonly destroyRef = inject(DestroyRef);

  parametro = new ParametroAzureAcsModel();
  salvando = false;
  carregando = true;
  readonly opcoesMoeda = [
    { id: 'BRL', nome: 'Real' },
    { id: 'USD', nome: 'Dólar' },
    { id: 'EUR', nome: 'Euro' }
  ];

  ngOnInit(): void {
    this.buscarParametros();
  }

  buscarParametros(): void {
    this.parametroService.buscar()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.parametro = res ?? new ParametroAzureAcsModel();
          this.parametro.ativo ??= true;
          this.parametro.moedaCusto ??= 'USD';
          this.carregando = false;
        },
        error: () => {
          this.carregando = false;
          this.msgService.msgErro('Erro ao buscar as configurações da Azure ACS');
        }
      });
  }

  converterEAtribuirLimiteHorario(valor: unknown): void {
    this.parametro.limiteHorario = this.converterInteiro(valor);
  }

  converterEAtribuirLimiteMinuto(valor: unknown): void {
    this.parametro.limiteMinuto = this.converterInteiro(valor);
  }

  atribuirValorUnitario(valor: unknown): void {
    this.parametro.valorUnitarioMsg = this.converterValorMonetario(valor);
  }

  selecionarMoeda(opcao?: { id: string }): void {
    if (opcao) this.parametro.moedaCusto = opcao.id as 'BRL' | 'USD' | 'EUR';
  }

  obterMoeda() {
    return this.opcoesMoeda.find(opcao => opcao.id === this.parametro.moedaCusto);
  }

  obterSimboloMoeda(): string {
    return this.parametro.moedaCusto === 'BRL'
      ? 'R$'
      : this.parametro.moedaCusto === 'EUR' ? '€' : 'US$';
  }

  salvar(): void {
    if (this.salvando || !this.validarCampos()) return;

    this.salvando = true;
    this.parametroService.salvar(this.parametro)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.salvando = false)
      )
      .subscribe({
        next: () => {
          this.msgService.msgSucesso('Configurações da Azure ACS salvas com sucesso');
          this.buscarParametros();
        },
        error: erro => this.msgService.msgErro(
          erro?.error?.reason ?? erro?.error?.message ?? 'Erro ao salvar as configurações da Azure ACS'
        )
      });
  }

  private validarCampos(): boolean {
    if (!this.validarTexto(this.parametro.endpoint, 256, 'Preencha o Endpoint da Azure ACS', 'O Endpoint deve ter no máximo 256 caracteres')) return false;
    if (this.parametro.apelido && this.parametro.apelido.length > 256) {
      this.msgService.msgAdvertencia('O Apelido deve ter no máximo 256 caracteres');
      return false;
    }
    if (!this.validarTexto(this.parametro.accessKey, 512, 'Preencha a Access Key', 'A Access Key deve ter no máximo 512 caracteres')) return false;
    if (!this.validarTexto(this.parametro.emailRemetente, 256, 'Preencha o E-mail de Envio', 'O E-mail de Envio deve ter no máximo 256 caracteres')) return false;
    if (!this.parametro.limiteHorario || this.parametro.limiteHorario <= 0) {
      this.msgService.msgAdvertencia('Preencha um Limite por Hora maior que zero');
      return false;
    }
    if (!this.parametro.limiteMinuto || this.parametro.limiteMinuto <= 0) {
      this.msgService.msgAdvertencia('Preencha um Limite por minuto maior que zero');
      return false;
    }
    if (this.parametro.valorUnitarioMsg == null || this.parametro.valorUnitarioMsg < 0) {
      this.msgService.msgAdvertencia('Preencha um Valor Unitário msg maior ou igual a zero');
      return false;
    }
    if (!this.parametro.moedaCusto) {
      this.msgService.msgAdvertencia('Selecione a moeda do valor unitário');
      return false;
    }
    if (!this.validarTexto(this.parametro.usuario, 256, 'Preencha o Usuário de acesso à API Azure ACS', 'O Usuário deve ter no máximo 256 caracteres')) return false;
    return this.validarTexto(this.parametro.senha, 256, 'Preencha a Senha de acesso à API Azure ACS', 'A Senha deve ter no máximo 256 caracteres');
  }

  private validarTexto(valor: string | undefined, maximo: number, mensagemObrigatorio: string, mensagemTamanho: string): boolean {
    if (!valor?.trim()) {
      this.msgService.msgAdvertencia(mensagemObrigatorio);
      return false;
    }
    if (valor.length > maximo) {
      this.msgService.msgAdvertencia(mensagemTamanho);
      return false;
    }
    return true;
  }

  private converterInteiro(valor: unknown): number | undefined {
    if (valor === '' || valor == null) return undefined;
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : undefined;
  }

  private converterValorMonetario(valor: unknown): number | undefined {
    if (valor === '' || valor == null) return undefined;
    const numero = typeof valor === 'number'
      ? valor
      : Number(String(valor).replace(/[^\d.-]/g, ''));
    return Number.isFinite(numero) ? numero : undefined;
  }
}
